import React from 'react';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import { RegionCode } from 'google-libphonenumber';
import { LiliAutoComplete, UserMessage } from '@lili/lili-frontend-common';
import { ControllerWrapper } from '../../../../hooks/providers/ControllerWrapper';
import { LiliTextInput } from '../../../../DesignSystem/LiliTextInput/LiliTextInput';
import { ExtendedUseFormReturn } from '../../../../hooks/providers/useFormHooks';
import Heading from '../../../../generic-components/Heading';
import { ReactComponent as BankIcon } from '../../../../assets/icons/svg/shared/ic-shared-bank.svg';
import {
  LiliInternationalPhoneNumberInput,
} from '../../../../DesignSystem/LiliInternationalPhoneNumberInput/LiliInternationalPhoneNumberInput';

import { IVendorModel, MoveMoneyTypes, UpdateVendorFormFieldsMap, UpdateVendorFormProps } from '../../interfaces';
import { useInternationalBaseFieldLogic } from './hooks/useInternationalBaseFieldLogic';
import { OtherBusinessSelection } from '../../components/OtherBusinessSelection/OtherBusinessSelection';

interface InternationalFieldsProps {
  formMethods: ExtendedUseFormReturn<UpdateVendorFormProps>
  dynamicCountry: string;
  moveType: MoveMoneyTypes;
  vendorData?: IVendorModel;
}

export const InternationalBaseFields = (props: InternationalFieldsProps) => {
  const { formMethods, vendorData, dynamicCountry, moveType } = props;
  const { t } = useTranslation();

  const {
    selectedCountryData,
    supportedCountriesList,
    isLoadingSwiftData,
    swiftData,
    resetSwiftData,
    handleSwiftBlur,
    shouldAvoidSwiftFetch,
    handleIsoCodeChange,
    isPhoneError,
    handlePhoneBlur,
    setIsPhoneError,
    handleIbanBlur,
    isOtherBusinessFlow,
    setIsOtherBusinessFlow,
  } = useInternationalBaseFieldLogic({ formMethods, dynamicCountry });

  const renderBankInfo = () => (isLoadingSwiftData ? <Skeleton height='71px' className='w-full min-h-[70px] fadeIn bg-bw02 border border-bw03 rounded-[8px] mb-[24px]' /> : (
    <div className='w-full fadeIn px-[8px] py-[12px] bg-bw02 border border-bw03 rounded-[8px] mb-[24px] flex items-center gap-[16px]'>
      <BankIcon className='text-bw07 min-w-[24px] stroke-bw07' />
      <div className='flex flex-col'>
        <Heading title={swiftData?.fullName || ''} className='font-metricRMedium text-bw07 text-[14px]' />
        <Heading title={`${swiftData?.city}, ${swiftData?.countryCode}`} className='font-metricR text-bw06 text-[14px]' />
      </div>
    </div>
  ));

  return (
    <>

      <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_COUNTRY} control={formMethods.control}>
        {({ onChange, value }) =>
          <div className='min-h-[76px] w-full'>
            <LiliAutoComplete
              placeholder={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.COUNTRY')}
              onItemSelection={(item: {title: string, value: string}) => onChange(item.value)}
              selectedItem={{ title: selectedCountryData?.name || '', value }}
              autoCompleteItems={supportedCountriesList}
            />
          </div>}
      </ControllerWrapper>

      <ControllerWrapper name={UpdateVendorFormFieldsMap.SWIFT} control={formMethods.control}>
        {({ onBlur, onChange, error, value }) => <LiliTextInput
          onClear={() => {
            resetSwiftData();
            shouldAvoidSwiftFetch.current = true;
          }}
          onChange={(val) => {
            if (swiftData) {
              resetSwiftData();
            }
            onChange(val.toUpperCase().trim());
          }}
          value={value}
          onBlur={() => {
            handleSwiftBlur(value, error?.message || '');
            onBlur();
          }}
          id={UpdateVendorFormFieldsMap.SWIFT}
          title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.SWIFT')}
          errorMessage={error?.message}
        />}
      </ControllerWrapper>

      {(isLoadingSwiftData || swiftData?.fullName) && renderBankInfo()}

      <ControllerWrapper name={UpdateVendorFormFieldsMap.IBAN} control={formMethods.control}>
        {({ onBlur, onChange, error, value }) => <LiliTextInput
          disabled={!dynamicCountry}
          onChange={(val) => onChange(val.toUpperCase().trim())}
          value={value}
          onBlur={() => {
            handleIbanBlur(value, error?.message || '');
            onBlur();
          }}
          id={UpdateVendorFormFieldsMap.IBAN}
          title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.IBAN')}
          errorMessage={error?.message}
        />}
      </ControllerWrapper>

      <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_PHONE} control={formMethods.control}>
        {({ onChange, value }) => <LiliInternationalPhoneNumberInput
          errorMessage={isPhoneError ? t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ERRORS.PHONE') : ''}
          onBlur={handlePhoneBlur}
          id=''
          fieldValidationStatus={(isValid) => {
            if (isValid) {
              setIsPhoneError(false);
            }
            formMethods.setValue(UpdateVendorFormFieldsMap.INTER_PHONE_STATUS, moveType !== MoveMoneyTypes.INTERNATIONAL_WIRE_OUT ? true : isValid);
            formMethods.trigger(UpdateVendorFormFieldsMap.INTER_PHONE_STATUS);
          }}
          onInputValueChange={(val) => {
            onChange(val);
          }}
          onIsoCodeChange={(isoCode: RegionCode) => handleIsoCodeChange(isoCode)}
          initialIsoCode={vendorData?.internationalWireAccountData?.phoneNumberCountryCode}
          inputValue={value}
        /> }
      </ControllerWrapper>

      <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_PHONE_STATUS} control={formMethods.control}>
        {({ value }) => <input className='hidden' type='checkbox' checked={value} /> }
      </ControllerWrapper>

      {isOtherBusinessFlow && (
        <UserMessage
          onClick={() => setIsOtherBusinessFlow(false)}
          id=''
        >
          <OtherBusinessSelection supportedCountries={supportedCountriesList} onClose={() => setIsOtherBusinessFlow(false)} />
        </UserMessage>
      )}

    </>
  );
};
